import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Filter } from 'lucide-react';
import { CASE_STUDIES } from '../mock';
import ClosingCTA from '../components/ClosingCTA';
import ClientLogo from '../components/ClientLogo';
import ProductMockup from '../components/ProductMockup';
import BrandCanvas, { hasBrandCanvas } from '../components/BrandCanvas';
import StrategyDiagram from '../components/StrategyDiagram';
import SEO from '../components/SEO';
// Category filters derived from case-study tags
const FILTERS = [
  { id: 'all',      label: 'All work' },
  { id: 'retail',   label: 'Retail & Commerce' },
  { id: 'run',      label: 'Run & Optimise' },
  { id: 'platform', label: 'Platform Modernisation' },
  { id: 'engineering', label: 'AI-Native Engineering' },
  { id: 'internal', label: 'Internal Programme' },
];

const tagToFilter = (tag) => {
  const t = tag.toLowerCase();
  if (t.includes('retail')) return 'retail';
  if (t.includes('run')) return 'run';
  if (t.includes('platform')) return 'platform';
  if (t.includes('engineering')) return 'engineering';
  if (t.includes('internal')) return 'internal';
  return 'all';
};

export default function Work() {
  const [active, setActive] = useState('all');

  const filtered = useMemo(() => {
    if (active === 'all') return CASE_STUDIES;
    return CASE_STUDIES.filter((c) => tagToFilter(c.tag) === active);
  }, [active]);

  const featured = CASE_STUDIES[0];
  const rest = filtered.filter((c) => c.slug !== featured.slug);

  return (
    <div>
      <SEO
        title="Work — case studies from enterprise programmes"
        description="Digital commerce and transformation programmes shipped for enterprises across MEA, Europe and Asia — Sephora, Al-Futtaim, MAGRABi, Fenix Outdoor, Wiiv, NorthLadder and more."
        canonical="https://digitalworks.co/work"
      />
      {/* HERO */}
      <section className="pt-20 pb-12">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F] mb-8">
            Client impact
          </div>
          <div className="grid grid-cols-12 gap-10 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h1 className="serif-tight text-[62px] md:text-[104px] xl:text-[124px] leading-[0.94]">
                Work that reached<br />
                <span className="text-[#22A94F]">production, at scale.</span>
              </h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-[16px] leading-relaxed text-[#1A1A1A]/75">
                Digital commerce and transformation programmes shipped for enterprises across MEA, Europe and Asia. Every engagement measured by what reached production, and how.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-4">
                {[
                  ['20+', 'Enterprise clients'],
                  ['12', 'Markets'],
                  ['0', 'PII crossed review'],
                ].map(([v, l]) => (
                  <div key={l} className="border-t border-[#1A1A1A]/15 pt-3">
                    <div className="serif-tight text-[28px] leading-none">{v}</div>
                    <div className="mt-2 text-[11px] mono uppercase tracking-widest text-[#1A1A1A]/55">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-12">
        <div className="dwx">
          <Link
            to={`/work/${featured.slug}`}
            className="group grid grid-cols-12 gap-8 rounded-[28px] bg-[#0F3D22] p-8 md:p-12 items-center lift"
          >
            <div className="col-span-12 md:col-span-5">
              <div className="rounded-[18px] bg-[#FBFAF6] border border-[#FBFAF6]/12 h-64 md:h-80 overflow-hidden">
                {hasBrandCanvas(featured.slug) ? (
                  <BrandCanvas slug={featured.slug} />
                ) : (
                  <ProductMockup variant={featured.mockupVariant} kicker={`${featured.tag.toUpperCase()} · CASE STUDY`} />
                )}
              </div>
            </div>
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]">
                Featured case · {featured.tag}
              </div>
              <div className="mt-5 h-16 flex items-end">
                <div
                  className="serif-tight leading-none text-[42px] md:text-[56px] text-[#FBFAF6]"
                  data-testid="work-featured-client"
                >
                  {featured.client.split(' · ')[0]}
                </div>
              </div>
              <div className="text-[13px] mono uppercase tracking-widest text-[#FBFAF6]/55 mt-2">
                {featured.sector} · {featured.markets}
              </div>
              <p className="text-[15px] leading-relaxed text-[#FBFAF6]/80 mt-6 max-w-xl">
                {featured.problem}
              </p>
              <div className="serif-tight text-[26px] text-[#22A94F] mt-6">
                {featured.outcome}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-[#22A94F] group-hover:text-[#7ED4A0] transition-colors">
                Read the full story <ArrowRight size={16} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FILTER + GRID */}
      <section className="py-12">
        <div className="dwx">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-6 border-b border-[#E8E4D9]">
            <div className="flex items-center gap-3 text-[#1A1A1A]/60">
              <Filter size={14} />
              <span className="mono text-[10px] tracking-[0.24em] uppercase">Browse by capability</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => {
                const on = active === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setActive(f.id)}
                    className={`px-4 py-2 rounded-full text-[12.5px] tracking-tight transition-colors ${
                      on ? 'bg-[#1A1A1A] text-[#FBFAF6]' : 'bg-transparent text-[#1A1A1A]/70 border border-[#E8E4D9] hover:border-[#1A1A1A]/30 hover:text-[#1A1A1A]'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {rest.length === 0 ? (
            <div className="text-center py-16 text-[15px] text-[#1A1A1A]/55">
              No case studies in that category yet — reset the filter to see all.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rest.map((c, i) => {
                // Alternate the card thumbnail media so the listing feels varied:
                // even indices show the brand canvas (delivered surface reference),
                // odd indices show the strategy diagram (architectural direction).
                // If neither variant exists for the slug, fall back to ProductMockup.
                const useCanvas = (i % 2 === 0) && hasBrandCanvas(c.slug);
                const useDiagram = !useCanvas;
                return (
                <Link
                  key={c.slug}
                  to={`/work/${c.slug}`}
                  className="group rounded-[20px] bg-[#F4F2EC] hover:bg-[#E8E4D9] transition-colors lift overflow-hidden flex flex-col"
                >
                  <div className="h-52 bg-[#FBFAF6] border-b border-[#E8E4D9] overflow-hidden card-thumb">
                    {useCanvas ? (
                      <BrandCanvas slug={c.slug} />
                    ) : useDiagram ? (
                      <StrategyDiagram slug={c.slug} />
                    ) : (
                      <ProductMockup variant={c.mockupVariant} kicker={c.tag.toUpperCase()} />
                    )}
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="mono text-[10px] tracking-[0.24em] uppercase text-[#22A94F]">{c.tag}</span>
                      <ArrowUpRight size={16} className="text-[#1A1A1A]/40 group-hover:text-[#22A94F] transition-colors" />
                    </div>
                    <div className="mt-6 h-14 flex items-center">
                      <ClientLogo logo={c.logo} name={c.client.split(' · ')[0]} size="sm" />
                    </div>
                    <div className="text-[11px] mono uppercase tracking-widest text-[#1A1A1A]/50 mt-2">{c.markets}</div>
                    <p className="text-[13.5px] leading-relaxed text-[#1A1A1A]/72 mt-4 line-clamp-3">
                      {c.problem}
                    </p>
                    <div className="mt-auto pt-5">
                      <div className="serif-tight text-[18px] leading-tight text-[#178838]">
                        {c.outcome}
                      </div>
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* IMPACT STRIP */}
      <section className="py-16 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F] mb-10">Outcomes across the estate</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              ['11 wks', 'Decision to production', 'Multi-market checkout rebuild, live across four regions'],
              ['38%',    'Faster resolution',      'Support ticket triage time reduced by more than a third'],
              ['14,000', 'Records reconciled',     'Legacy inconsistencies resolved with zero customer-facing downtime'],
              ['3 wks',  'New market onboarding',  'From strategy sign-off to launch, down from 14 weeks'],
            ].map(([v, c, l], i) => (
              <div key={i} className="border-t-2 border-[#22A94F] pt-6">
                <div className="serif-tight text-[46px] leading-none">{v}</div>
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#178838] mt-3">{c}</div>
                <div className="text-[12.5px] leading-relaxed text-[#1A1A1A]/70 mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
